import { useEffect, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { useAppDispatch } from "@/store"
import { scheduleOverridesApi, useGetBookingMoveBatchQuery } from "../scheduleOverridesApi"
import { decodeBatchStatus, isBatchFinished } from "./bookingMoveBatch"

/** How often a running batch is asked after: as the slot-plan jobs, every few seconds. */
export const BATCH_POLL_MS = 3000

/**
 * A booking-move batch a page is waiting on (AB#809; used by automatic reassignment and the manual
 * workspace): polled until it is Succeeded or Failed — the "booking-move-batch" notification drops its
 * cache entry, so the end shows at once — then the bookings and overrides are dropped from the cache,
 * since appointments have moved.
 *
 * Copies useSlotPlanJob: the query is skipped with no batch, and stopped by taking its interval to zero
 * once the batch has finished; it keeps polling in a background tab so the answer is there on return.
 */
export function useBookingMoveBatch(batchId: string | null, pollMs: number = BATCH_POLL_MS) {
  const dispatch = useAppDispatch()
  const [polling, setPolling] = useState(true)

  // Another batch: poll it afresh.
  useEffect(() => {
    setPolling(true)
  }, [batchId])

  const query = useGetBookingMoveBatchQuery(batchId ?? skipToken, {
    pollingInterval: polling ? pollMs : 0,
    skipPollingIfUnfocused: false,
  })

  // currentData, not data: data still holds the last batch while a new one is being read.
  const batch = query.currentData && query.currentData.id === batchId ? query.currentData : undefined
  const status = decodeBatchStatus(batch?.status)
  const finished = isBatchFinished(status)

  useEffect(() => {
    if (!batchId || !finished) return
    setPolling(false)
    dispatch(scheduleOverridesApi.util.invalidateTags(["AmsBookings", "AmsScheduleOverrides"]))
  }, [finished, batchId, dispatch])

  // A batch the server does not know: nothing to wait for.
  const notFound = (query.error as { status?: number } | undefined)?.status === 404
  useEffect(() => {
    if (notFound) setPolling(false)
  }, [notFound])

  return {
    batch,
    status,
    /** Queued or running: the page waits for it before offering the moves again. */
    isRunning: batchId !== null && !finished && !notFound,
    notFound,
    error: notFound ? undefined : query.error,
  }
}
